import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { validateLoginForm, validateSignupForm } from '../../utils/validation';
import { onboardingSignup, onboardingLogin } from '../../services/authService';
import Input from '../../components/forms/Input';
import Button from '../../components/forms/Button';
import { Mail, Smartphone, CheckCircle, XCircle, Clock, AlertCircle, Search, Lock, RefreshCw, Target } from 'lucide-react';
import './Login.css';

const Login = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    password: '',
    emailVerificationData: null,
    mobileVerificationData: null
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [verificationStep, setVerificationStep] = useState(''); // 'email' or 'mobile'
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: '' }));
    }
  };

  const handlePhoneChange = (value) => {
    console.log('📱 Phone input changed to:', value);
    setFormData((prev) => ({ ...prev, mobile: value }));
    if (errors.mobile) {
      setErrors((prev) => ({ ...prev, mobile: '' }));
    }
  };

  // Function to verify email using MSG91 OTP
  const verifyEmail = async () => {
    if (!formData.email) {
      setApiError('Email is required');
      setIsLoading(false);
      return;
    }

    console.log('📧 ========== EMAIL VERIFICATION START ==========');
    console.log('Email to verify:', formData.email);
    setVerificationStep('email');

    const configuration = {
      widgetId: "33686e6c6f5a303333333730",
      tokenAuth: "381161TFBvL0GMACH64d68d81P1",
      identifier: formData.email,
      exposeMethods: "false",
      success: (data) => {
        console.log('✅ ========== EMAIL OTP SUCCESS ==========');
        console.log('Success data type:', typeof data);
        console.log('Success data:', data);
        console.log('Success data stringified:', JSON.stringify(data, null, 2));
        
        if (data && typeof data === 'object') {
          console.log('Success data keys:', Object.keys(data));
          Object.keys(data).forEach(key => {
            console.log(`  ${key}:`, data[key]);
          });
        }
        
        // Store the verification data
        setFormData((prev) => ({ 
          ...prev, 
          emailVerificationData: data 
        }));
        
        setApiError('');
        setVerificationStep('');
        
        console.log('✅ Email verified successfully! Moving to mobile verification...');
        
        // Wait a bit before triggering mobile verification
        setTimeout(() => {
          verifyMobile();
        }, 800);
      },
      failure: (error) => {
        console.error('❌ ========== EMAIL OTP FAILURE ==========');
        console.error('Error type:', typeof error);
        console.error('Error value:', error);
        console.error('Error stringified:', JSON.stringify(error, null, 2));
        
        if (error && typeof error === 'object') {
          console.error('Error keys:', Object.keys(error));
          console.error('error.message:', error.message);
          console.error('error.error:', error.error);
          console.error('error.msg:', error.msg);
          console.error('error.type:', error.type);
          console.error('error.code:', error.code);
          console.error('error.data:', error.data);
        }
        
        // Parse the error message
        let errorMessage = 'Email verification failed. Please try again.';
        
        if (error) {
          if (typeof error === 'string') {
            errorMessage = error;
          } else if (error.message) {
            errorMessage = error.message;
          } else if (error.error) {
            errorMessage = error.error;
          } else if (error.msg) {
            errorMessage = error.msg;
          }
          
          // Check for specific error types
          const errorStr = String(errorMessage).toLowerCase();
          if (errorStr.includes('invalid') || errorStr.includes('incorrect') || errorStr.includes('wrong')) {
            errorMessage = 'Invalid OTP for email. Please check and try again.';
          } else if (errorStr.includes('expired')) {
            errorMessage = 'OTP has expired. Please request a new one.';
          } else if (errorStr.includes('max') || errorStr.includes('limit')) {
            errorMessage = 'Maximum attempts exceeded. Please try again later.';
          }
        }
        
        console.error('Final error message:', errorMessage);
        setApiError(errorMessage);
        setIsLoading(false);
        setVerificationStep('');
      },
    };

    // Check if initSendOTP is available
    if (typeof window.initSendOTP === 'function') {
      try {
        console.log('📧 Initializing MSG91 for email...');
        window.initSendOTP(configuration);
      } catch (err) {
        console.error('❌ Error initializing email OTP:', err);
        setApiError('Failed to initialize OTP service. Please refresh the page.');
        setIsLoading(false);
        setVerificationStep('');
      }
    } else {
      console.error('❌ window.initSendOTP is not available');
      console.error('Available window properties:', Object.keys(window).filter(k => k.includes('init') || k.includes('send') || k.includes('otp')));
      setApiError('OTP service not loaded. Please refresh the page and ensure you have internet connection.');
      setIsLoading(false);
      setVerificationStep('');
    }
  };

  // Function to verify mobile using MSG91 OTP
  const verifyMobile = async () => {
    if (!formData.mobile) {
      setApiError('Mobile number is required');
      setIsLoading(false);
      return;
    }

    console.log('📱 ========== MOBILE VERIFICATION START ==========');
    console.log('Mobile number to verify:', formData.mobile);
    console.log('Mobile number length:', formData.mobile.length);
    console.log('Mobile number starts with:', formData.mobile.substring(0, 2));
    setVerificationStep('mobile');

    // Ensure mobile has country code
    let mobileWithCode = formData.mobile;

    // Remove any non-numeric characters first
    mobileWithCode = mobileWithCode.replace(/\D/g, '');
    console.log('🧹 After removing non-numeric chars:', mobileWithCode);

    // Add country code if not present
    if (!mobileWithCode.startsWith('91') && mobileWithCode.length === 10) {
      mobileWithCode = '91' + mobileWithCode;
      console.log('⚠️ Added country code. New mobile:', mobileWithCode);
    }

    // Validate final format
    console.log('📱 Final mobile format:', mobileWithCode);
    console.log('📱 Final mobile length:', mobileWithCode.length);

    if (mobileWithCode.length !== 12) {
      console.error('❌ Invalid mobile number length. Expected 12, got:', mobileWithCode.length);
      setApiError('Invalid mobile number format. Please check and try again.');
      setIsLoading(false);
      setVerificationStep('');
      return;
    }

    // Clean up any existing MSG91 elements
    console.log('🧹 Cleaning up existing MSG91 widgets...');
    const existingWidgets = document.querySelectorAll('.send-otp-main-container');
    console.log('Found', existingWidgets.length, 'existing widgets');
    existingWidgets.forEach(widget => widget.remove());

    // Small delay to ensure DOM is clean
    await new Promise(resolve => setTimeout(resolve, 200));

    const configuration = {
      widgetId: "33686c616466373936303135",
      tokenAuth: "381161TFBvL0GMACH64d68d81P1",
      identifier: mobileWithCode,
      exposeMethods: "false",
      success: (data) => {
        console.log('✅ ========== MOBILE OTP SUCCESS ==========');
        console.log('Success data type:', typeof data);
        console.log('Success data:', data);
        console.log('Success data stringified:', JSON.stringify(data, null, 2));

        if (data && typeof data === 'object') {
          console.log('Success data keys:', Object.keys(data));
          Object.keys(data).forEach(key => {
            console.log(`  ${key}:`, data[key]);
          });
        }

        // Store the verification data with properly formatted mobile number
        setFormData((prev) => ({
          ...prev,
          mobileVerificationData: data,
          mobile: mobileWithCode // Update with properly formatted country code
        }));

        setApiError('');
        setVerificationStep('');

        console.log('✅ Mobile verified successfully! Completing signup...');
        console.log('📱 Using mobile number:', mobileWithCode);

        // After mobile verification, complete signup
        completeSignup(data);
      },
      failure: (error) => {
        console.error('❌ ========== MOBILE OTP FAILURE ==========');
        console.error('📱 Mobile number used for verification:', mobileWithCode);
        console.error('Error type:', typeof error);
        console.error('Error value:', error);

        // Try to stringify the error, but catch any circular reference errors
        try {
          console.error('Error stringified:', JSON.stringify(error, null, 2));
        } catch (e) {
          console.error('Error could not be stringified (circular reference?)');
        }

        console.error('Error constructor:', error?.constructor?.name);

        if (error && typeof error === 'object') {
          console.error('Error keys:', Object.keys(error));
          console.error('error.message:', error.message);
          console.error('error.error:', error.error);
          console.error('error.msg:', error.msg);
          console.error('error.type:', error.type);
          console.error('error.code:', error.code);
          console.error('error.data:', error.data);
          console.error('error.response:', error.response);
          console.error('error.status:', error.status);

          // Try to access nested properties
          if (error.response) {
            console.error('error.response.data:', error.response.data);
            console.error('error.response.message:', error.response.message);
          }
        }

        // Parse the error message
        let errorMessage = 'Mobile verification failed. Please try again.';

        if (error) {
          if (typeof error === 'string') {
            errorMessage = error;
          } else if (error.message) {
            errorMessage = error.message;
          } else if (error.error) {
            errorMessage = error.error;
          } else if (error.msg) {
            errorMessage = error.msg;
          } else if (error.data) {
            errorMessage = error.data;
          } else if (error.response?.message) {
            errorMessage = error.response.message;
          } else if (error.response?.data) {
            errorMessage = error.response.data;
          }

          // Check for specific error types
          const errorStr = String(errorMessage).toLowerCase();
          if (errorStr.includes('invalid') || errorStr.includes('incorrect') || errorStr.includes('wrong')) {
            errorMessage = 'Invalid OTP for mobile. Please check the OTP and try again. Make sure you entered the correct code received on: ' + mobileWithCode;
          } else if (errorStr.includes('expired')) {
            errorMessage = 'OTP has expired. Please request a new one.';
          } else if (errorStr.includes('max') || errorStr.includes('limit')) {
            errorMessage = 'Maximum attempts exceeded. Please try again later.';
          } else if (errorStr.includes('not found')) {
            errorMessage = 'Mobile number not found. Please check and try again.';
          }
        }

        console.error('Final error message:', errorMessage);
        console.error('🔍 Troubleshooting tips:');
        console.error('1. Verify mobile number format is correct: ' + mobileWithCode);
        console.error('2. Check if OTP was received on this number');
        console.error('3. Make sure you entered the OTP correctly');
        console.error('4. Check MSG91 dashboard for widget configuration');

        setApiError(errorMessage);
        setIsLoading(false);
        setVerificationStep('');
      },
    };

    // Check if initSendOTP is available
    if (typeof window.initSendOTP === 'function') {
      try {
        console.log('📱 Initializing MSG91 for mobile...');
        console.log('Configuration:', {
          widgetId: configuration.widgetId,
          identifier: configuration.identifier,
          tokenAuth: configuration.tokenAuth.substring(0, 10) + '...'
        });
        window.initSendOTP(configuration);
      } catch (err) {
        console.error('❌ Error initializing mobile OTP:', err);
        console.error('Error stack:', err.stack);
        setApiError('Failed to initialize OTP service. Please refresh the page.');
        setIsLoading(false);
        setVerificationStep('');
      }
    } else {
      console.error('❌ window.initSendOTP is not available');
      console.error('Available window properties:', Object.keys(window).filter(k => k.includes('init') || k.includes('send') || k.includes('otp')));
      setApiError('OTP service not loaded. Please refresh the page and ensure you have internet connection.');
      setIsLoading(false);
      setVerificationStep('');
    }
  };

  // Function to complete signup after OTP verification
  const completeSignup = async (mobileVerificationData) => {
    console.log('🎯 ========== COMPLETING SIGNUP ==========');
    
    try {
      const signupData = {
        name: formData.fullName,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        emailVerified: true,
        mobileVerified: true,
        emailVerificationData: formData.emailVerificationData,
        mobileVerificationData: mobileVerificationData
      };

      console.log('Sending signup data to backend:', {
        ...signupData,
        password: '***hidden***',
        emailVerificationData: signupData.emailVerificationData ? 'present' : 'missing',
        mobileVerificationData: signupData.mobileVerificationData ? 'present' : 'missing'
      });
      
      const response = await onboardingSignup(signupData);

      console.log('✅ Signup Success:', response);
      
      setApiError('');
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Signup Error:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = 'Signup failed. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('🚀 ========== FORM SUBMIT ==========');
    console.log('Is Sign Up:', isSignUp);
    console.log('Form Data:', {
      ...formData,
      password: '***hidden***'
    });

    // Clear previous errors
    setApiError('');

    const validationErrors = isSignUp
      ? validateSignupForm(formData)
      : validateLoginForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      console.error('❌ Validation errors:', validationErrors);
      setErrors(validationErrors);
      return;
    }

    console.log('✅ Validation passed');
    setIsLoading(true);

    try {
      if (isSignUp) {
        console.log('🔐 Starting signup process...');
        verifyEmail();
      } else {
        console.log('🔐 Starting login process...');
        
        const response = await onboardingLogin({
          email: formData.email,
          password: formData.password,
        });

        console.log('✅ Login Success:', response);
        
        setApiError('');
        navigate('/dashboard');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('❌ Authentication Error:', error);
      
      let errorMessage = 'Authentication failed. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setApiError(errorMessage);
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    console.log('🔄 Toggling mode from', isSignUp ? 'Sign Up' : 'Sign In', 'to', !isSignUp ? 'Sign Up' : 'Sign In');
    setIsSignUp(!isSignUp);
    setFormData({
      fullName: '',
      email: '',
      mobile: '',
      password: '',
      emailVerificationData: null,
      mobileVerificationData: null
    });
    setErrors({});
    setApiError('');
    setVerificationStep('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className={`login-card ${isSignUp ? 'sign-up-mode' : ''}`}>
          {/* Sign In Form */}
          <div className="form-section signin-section">
            <form onSubmit={handleSubmit} className="login-form">
              <h2 className="form-title">Sign In</h2>
              <p className="form-subtitle">Welcome back! Please login to your account.</p>

              {!isSignUp && apiError && (
                <div style={{
                  backgroundColor: '#fee',
                  color: '#c33',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  fontSize: '14px',
                  textAlign: 'center',
                  border: '1px solid #fcc'
                }}>
                  {apiError}
                </div>
              )}

              <Input
                id="email"
                type="email"
                label="Email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                error={errors.email}
                required
                disabled={isLoading}
              />

              <Input
                id="password"
                type="password"
                label="Password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange}
                error={errors.password}
                required
                disabled={isLoading}
              />

              <div className="form-footer">
                <a href="#" className="forgot-password">
                  Forgot Password?
                </a>
              </div>

              <Button type="submit" variant="primary" fullWidth disabled={isLoading}>
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>

              <p className="toggle-text">
                Don't have an account?{' '}
                <button 
                  type="button" 
                  className="toggle-button" 
                  onClick={toggleMode}
                  disabled={isLoading}
                >
                  Sign Up
                </button>
              </p>
            </form>
          </div>

          {/* Sign Up Form */}
          <div className="form-section signup-section">
            <form onSubmit={handleSubmit} className="login-form">
              <h2 className="form-title">Sign Up</h2>
              <p className="form-subtitle">Create your account to get started.</p>

              {isSignUp && apiError && (
                <div style={{
                  backgroundColor: '#fee',
                  color: '#c33',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  fontSize: '14px',
                  textAlign: 'center',
                  border: '1px solid #fcc'
                }}>
                  {apiError}
                </div>
              )}

              {isSignUp && verificationStep && (
                <div style={{
                  backgroundColor: '#e7f3ff',
                  color: '#0066cc',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  fontSize: '14px',
                  textAlign: 'center',
                  border: '1px solid #b3d9ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  {verificationStep === 'email' ? (
                    <>
                      <Mail size={16} />
                      <span>Verifying email...</span>
                    </>
                  ) : (
                    <>
                      <Smartphone size={16} />
                      <span>Verifying mobile...</span>
                    </>
                  )}
                </div>
              )}

              <Input
                id="fullName"
                type="text"
                label="Full Name"
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={handleInputChange}
                error={errors.fullName}
                required
                disabled={isLoading}
              />

              <Input
                id="email"
                type="email"
                label="Email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                error={errors.email}
                required
                disabled={isLoading}
              />

              <div className="form-group" style={{ marginBottom: '1.5rem', width: '100%', boxSizing: 'border-box' }}>
                <label htmlFor="mobile" className="form-label" style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Mobile
                </label>
                <PhoneInput
                  country={'in'}
                  value={formData.mobile}
                  onChange={handlePhoneChange}
                  placeholder="Enter your mobile number"
                  enableSearch={true}
                  countryCodeEditable={false}
                  disabled={isLoading}
                  inputProps={{
                    name: 'mobile',
                    required: true,
                    autoFocus: false
                  }}
                  containerStyle={{
                    width: '100%'
                  }}
                  inputStyle={{
                    width: '100%',
                    height: '45px',
                    fontSize: '14px',
                    paddingLeft: '48px',
                    border: errors.mobile ? '1px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: isLoading ? '#f5f5f5' : '#fff'
                  }}
                  buttonStyle={{
                    border: errors.mobile ? '1px solid #dc3545' : '1px solid #ddd',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    backgroundColor: isLoading ? '#f5f5f5' : '#fff'
                  }}
                  dropdownStyle={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    maxWidth: '280px',
                    overflowX: 'hidden'
                  }}
                />
                {errors.mobile && (
                  <div className="error-message" style={{ 
                    color: '#dc3545', 
                    fontSize: '0.875rem', 
                    marginTop: '0.5rem',
                    display: 'block'
                  }}>
                    {errors.mobile}
                  </div>
                )}
              </div>

              <Input
                id="password"
                type="password"
                label="Password"
                placeholder="Create a password"
                value={formData.password}
                onChange={handleInputChange}
                error={errors.password}
                required
                disabled={isLoading}
              />

              <Button type="submit" variant="primary" fullWidth disabled={isLoading}>
                {isLoading ? 'Creating Account...' : 'Sign Up'}
              </Button>

              <p className="toggle-text">
                Already have an account?{' '}
                <button 
                  type="button" 
                  className="toggle-button" 
                  onClick={toggleMode}
                  disabled={isLoading}
                >
                  Sign In
                </button>
              </p>
            </form>
          </div>

          {/* Overlay Panels */}
          <div className="overlay-container">
            <div className="overlay">
              <div className="overlay-panel overlay-left">
                <h2>Welcome Back!</h2>
                <p>To keep connected with us please login with your personal info</p>
                <button className="btn-ghost" onClick={toggleMode} disabled={isLoading}>
                  Sign In
                </button>
              </div>
              <div className="overlay-panel overlay-right">
                <h2>Hello, Friend!</h2>
                <p>Enter your details and start your journey with us</p>
                <button className="btn-ghost" onClick={toggleMode} disabled={isLoading}>
                  Sign Up
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;